import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Zap, Target, TrendingUp, Bell, Users, BarChart3, Gift, Rocket, DollarSign, Trophy, Star, Clock, CreditCard } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useAuth, API } from "../App";
import Navbar, { AuthModal } from "../components/Navbar";
import axios from "axios";
import SEO from "../components/SEO";
import { toast } from "sonner";

const FeatureItem = ({ children }) => (
  <li className="flex items-start gap-3">
    <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
    <span className="text-zinc-300">{children}</span>
  </li>
);

const PricingPage = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authMode, setAuthMode] = useState("login");
  const [promoCode, setPromoCode] = useState("");
  const [activatingTrial, setActivatingTrial] = useState(false);
  const [trackRecord, setTrackRecord] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await axios.get(`${API}/track-record`);
        setTrackRecord(res.data);
      } catch (error) {
        console.warn("Failed to fetch track record");
      }
    };
    fetchStats();
  }, []);

  const handleActivateTrial = async () => {
    if (!user) {
      setAuthMode("register");
      setAuthModalOpen(true);
      toast.info("Create a free account first, then enter your promo code");
      return;
    }

    if (!promoCode.trim()) {
      toast.error("Please enter a promo code");
      return;
    }

    setActivatingTrial(true);
    try {
      const res = await axios.post(`${API}/auth/activate-trial`, { promo_code: promoCode });
      toast.success(res.data.message);
      if (refreshUser) await refreshUser();
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid promo code");
    } finally {
      setActivatingTrial(false);
    }
  };

  const handleSubscribe = async () => {
    if (!user) {
      setAuthMode("register");
      setAuthModalOpen(true);
      return;
    }

    try {
      const res = await axios.post(`${API}/payments/checkout`, { plan: "hsl_monthly" });
      window.location.href = res.data.url;
    } catch (error) {
      toast.error("Failed to start checkout. Please try again.");
    }
  };

  const isPremium = user?.subscription_tier === "premium" || user?.subscription_tier === "vip";
  const hasUsedTrial = user?.trial_used;

  return (
    <div className="min-h-screen bg-hsl-bg" data-testid="pricing-page">
      <Navbar 
        onLoginClick={() => { setAuthMode("login"); setAuthModalOpen(true); }}
        onRegisterClick={() => { setAuthMode("register"); setAuthModalOpen(true); }}
      />
      
      <SEO 
        title="Premium Picks - $149.99/month | HSL.bet"
        description="Get access to all HSL premium picks. 7-day free trial available with promo code. Join the team that turned $0 to $100K+."
        keywords="sports picks subscription, premium sports picks, HSL picks, sports betting"
      />

      <main className="pt-20 pb-16">
        {/* Hero Section */}
        <section className="py-16 px-4">
          <div className="max-w-5xl mx-auto text-center">
            {/* Success Badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/10 border border-green-500/30 mb-8"
            >
              <Trophy className="w-5 h-5 text-green-400" />
              <span className="text-green-400 font-semibold">$100K Goal Achieved!</span>
              <Link to="/road-to-100k" className="text-green-400 underline hover:text-green-300 ml-1">View Results →</Link>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="font-heading text-4xl md:text-6xl font-black uppercase tracking-tight mb-6"
            >
              <span className="text-white">Join the</span>{" "}
              <span className="text-gradient">Winning Team</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-xl text-zinc-400 mb-4 max-w-2xl mx-auto"
            >
              We turned data-driven analysis into <span className="text-green-400 font-bold">$102,857</span> in verified profit.
              Now get the same picks that made it happen.
            </motion.p>

            {/* Stats Row */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="flex justify-center gap-8 md:gap-16 mb-12"
            >
              <div className="text-center">
                <p className="font-mono text-3xl md:text-4xl font-bold text-green-400">+$102K</p>
                <p className="text-zinc-500 text-sm">Total Profit</p>
              </div>
              <div className="text-center">
                <p className="font-mono text-3xl md:text-4xl font-bold text-hsl-primary">{trackRecord?.win_rate || 60}%</p>
                <p className="text-zinc-500 text-sm">Win Rate</p>
              </div>
              <div className="text-center">
                <p className="font-mono text-3xl md:text-4xl font-bold text-white">{trackRecord?.total_picks || "350"}+</p>
                <p className="text-zinc-500 text-sm">Verified Picks</p>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Pricing Cards */}
        <section className="py-8 px-4">
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              
              {/* Free Trial Card */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-gradient-to-br from-hsl-surface to-hsl-surface/50 border-2 border-hsl-primary/50 rounded-2xl p-8 relative overflow-hidden"
              >
                <div className="absolute top-4 right-4">
                  <span className="bg-hsl-primary/20 text-hsl-primary text-xs font-bold px-3 py-1 rounded-full uppercase">
                    Try Free
                  </span>
                </div>
                
                <div className="mb-6">
                  <Clock className="w-10 h-10 text-hsl-primary mb-4" />
                  <h3 className="font-heading text-2xl font-bold text-white uppercase mb-2">7-Day Free Trial</h3>
                  <p className="text-zinc-400">Full access for one week. No credit card required.</p>
                </div>

                <div className="mb-6">
                  <div className="flex items-baseline gap-2 mb-4">
                    <span className="font-mono text-5xl font-black text-white">$0</span>
                    <span className="text-zinc-500">for 7 days</span>
                  </div>
                </div>

                <ul className="space-y-3 mb-8">
                  <FeatureItem>All premium picks for 7 days</FeatureItem>
                  <FeatureItem>Full analysis & reasoning</FeatureItem>
                  <FeatureItem>Email & SMS notifications</FeatureItem>
                  <FeatureItem>No credit card required</FeatureItem>
                </ul>

                {isPremium ? (
                  <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 text-center">
                    <Check className="w-6 h-6 text-green-400 mx-auto mb-2" />
                    <p className="text-green-400 font-semibold">You have premium access!</p>
                  </div>
                ) : hasUsedTrial ? (
                  <div className="bg-zinc-500/10 border border-zinc-500/30 rounded-lg p-4 text-center">
                    <p className="text-zinc-400">Trial already used on this account</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <Input
                      placeholder="Enter promo code (HSLTRIAL)"
                      value={promoCode}
                      onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                      className="input-hsl text-center font-mono uppercase"
                      data-testid="promo-code-input"
                    />
                    <Button
                      onClick={handleActivateTrial}
                      disabled={activatingTrial}
                      className="w-full bg-hsl-primary hover:bg-hsl-primary/90 text-black font-bold py-6"
                      data-testid="activate-trial-btn"
                    >
                      {activatingTrial ? "Activating..." : "Start Free Trial"}
                    </Button>
                    <p className="text-zinc-500 text-xs text-center">
                      Use code <span className="text-hsl-primary font-mono">HSLTRIAL</span> for 7 days free
                    </p>
                  </div>
                )}
              </motion.div>

              {/* Monthly Subscription Card */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 }}
                className="bg-gradient-to-br from-yellow-500/10 via-hsl-surface to-hsl-surface border-2 border-yellow-500/50 rounded-2xl p-8 relative overflow-hidden"
              >
                <div className="absolute top-4 right-4">
                  <span className="bg-yellow-500/20 text-yellow-400 text-xs font-bold px-3 py-1 rounded-full uppercase">
                    Most Popular
                  </span>
                </div>
                
                <div className="mb-6">
                  <Star className="w-10 h-10 text-yellow-400 mb-4" />
                  <h3 className="font-heading text-2xl font-bold text-white uppercase mb-2">HSL Premium</h3>
                  <p className="text-zinc-400">Full access to all picks and analysis.</p>
                </div>

                <div className="mb-6">
                  <div className="flex items-baseline gap-2 mb-1">
                    <span className="font-mono text-5xl font-black text-white">$149</span>
                    <span className="font-mono text-2xl text-white">.99</span>
                    <span className="text-zinc-500">/month</span>
                  </div>
                  <p className="text-zinc-500 text-sm">Cancel anytime</p>
                </div>

                <ul className="space-y-3 mb-8">
                  <FeatureItem>Every pick we make - Free, Premium, & Hammer</FeatureItem>
                  <FeatureItem>Detailed analysis with Situational Edge tags</FeatureItem>
                  <FeatureItem>Instant email & SMS alerts</FeatureItem>
                  <FeatureItem>Full track record & stats access</FeatureItem>
                  <FeatureItem>CLV (Closing Line Value) tracking</FeatureItem>
                </ul>

                {isPremium && !user?.trial_expires ? (
                  <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 text-center">
                    <Check className="w-6 h-6 text-green-400 mx-auto mb-2" />
                    <p className="text-green-400 font-semibold">You're subscribed!</p>
                  </div>
                ) : (
                  <Button
                    onClick={handleSubscribe}
                    className="w-full bg-yellow-500 hover:bg-yellow-400 text-black font-bold py-6"
                    data-testid="subscribe-btn"
                  >
                    <CreditCard className="w-5 h-5 mr-2" />
                    Subscribe Now
                  </Button>
                )}
              </motion.div>
            </div>
          </div>
        </section>

        {/* What You Get Section */}
        <section className="py-16 px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-center text-white uppercase mb-12">
              What's Included
            </h2>
            
            <div className="grid md:grid-cols-3 gap-6">
              {[
                {
                  icon: Target,
                  title: "All Pick Tiers",
                  description: "Access every pick including our highest-conviction Hammer plays that average the biggest profits."
                },
                {
                  icon: BarChart3,
                  title: "Full Analysis",
                  description: "Detailed reasoning behind every pick with our Situational Edge system tracking 6 key factors."
                },
                {
                  icon: TrendingUp,
                  title: "Live Results",
                  description: "Complete transparency with verified results, CLV tracking, and performance by sport/tier."
                },
                {
                  icon: Bell,
                  title: "Instant Alerts",
                  description: "Get notified via email, SMS, or push the moment we release a new pick."
                },
                {
                  icon: Trophy,
                  title: "Proven Results",
                  description: "$102K+ in documented profit. Every pick tracked from the moment it's posted."
                },
                {
                  icon: Zap,
                  title: "All Sports",
                  description: "NFL, NBA, WNBA, MLB, NHL, NCAAF, NCAAB, Soccer, UFC, Tennis, and PGA coverage."
                }
              ].map((feature, i) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  className="bg-hsl-surface/50 border border-white/10 rounded-xl p-6 hover:border-hsl-primary/30 transition-all"
                >
                  <feature.icon className="w-10 h-10 text-hsl-primary mb-4" />
                  <h3 className="text-white font-bold text-lg mb-2">{feature.title}</h3>
                  <p className="text-zinc-400 text-sm">{feature.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 px-4 bg-hsl-surface/30">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-heading text-3xl font-bold text-center text-white uppercase mb-12">
              Questions?
            </h2>
            
            <div className="space-y-6">
              {[
                {
                  q: "How does the 7-day free trial work?",
                  a: "Create a free account, enter the promo code HSLTRIAL, and you'll get 7 days of full premium access. No credit card required. After 7 days, you can subscribe or continue as a free user."
                },
                {
                  q: "What's included in the subscription?",
                  a: "You get every pick we make including Free Plays, Members Only, and Hammer picks. Plus full analysis, instant notifications, and access to all our tracking tools."
                },
                {
                  q: "What sports do you cover?",
                  a: "NFL, NBA, WNBA, MLB, NHL, College Football, College Basketball, Soccer, UFC, Tennis, and PGA. We go where the value is."
                },
                {
                  q: "Can I cancel anytime?",
                  a: "Yes, cancel anytime with no questions asked. You'll keep access until the end of your billing period."
                },
                {
                  q: "How do I know your picks are real?",
                  a: "Every pick is timestamped before the game starts. Check our Results page for complete, verified results. We documented our entire Road to $100K journey."
                }
              ].map((faq, i) => (
                <div key={i} className="bg-hsl-surface border border-white/10 rounded-xl p-6">
                  <h3 className="text-white font-semibold mb-2">{faq.q}</h3>
                  <p className="text-zinc-400 text-sm">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 px-4">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="font-heading text-3xl font-bold text-white uppercase mb-4">
              Ready to Start Winning?
            </h2>
            <p className="text-zinc-400 mb-8">
              Try HSL free for 7 days with code <span className="text-hsl-primary font-mono font-bold">HSLTRIAL</span>
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => {
                  if (!user) {
                    setAuthMode("register");
                    setAuthModalOpen(true);
                  } else {
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }
                }}
                className="btn-primary-glow px-8 py-6 text-lg"
              >
                Start Free Trial
              </Button>
              <Link to="/track-record">
                <Button variant="outline" className="bg-white/5 border-white/20 text-white hover:bg-white/10 px-8 py-6 text-lg">
                  View Results
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </main>

      <AuthModal 
        isOpen={authModalOpen} 
        onClose={() => setAuthModalOpen(false)} 
        mode={authMode}
        setMode={setAuthMode}
      />
    </div>
  );
};

export default PricingPage;
